// Puente entre las pantallas y el motor compartido (packages/energia-motor).
// Las pantallas editan la instalación en el formato del archivo v2 (nombres de Sustemia: los mismos que usan el motor,
// sus validaciones y sus textos) y guardan convirtiendo a Mongo con la misma traducción que usa el servidor.
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import motor from "energia-motor";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { EnergyPerformanceInstallationform } from "../../../../api";

export const P = motor.prototipo;
export { motor };

const energyPerformanceCtrl = new EnergyPerformanceInstallationform();

// Uso del módulo (hoja 16): A · Informe de desempeño / B · Implementar ISO 50001. Lo fija el workspace.
export const ModoContext = createContext("A");
export const useModo = () => useContext(ModoContext);

// Solo lectura: las pantallas se ven y se descargan, pero no guardan nada. Motivo:
//  - "ejemplo": uno de los 22 ejemplos del módulo (globales; hoja 23 p. 27);
//  - "permiso": el usuario no tiene permiso para modificar (por ahora solo el administrador; hoja 23 p. 24-26 y 66).
export const SoloLecturaContext = createContext(false);
export const useSoloLectura = () => useContext(SoloLecturaContext);
const AVISO = { ejemplo: ["energy_performance_readonly_blocked", "energy_performance_readonly_note"], permiso: ["energy_performance_no_permission", "energy_performance_no_permission"] };
export const textoSoloLectura = (t, motivo) => t((AVISO[motivo] || AVISO.ejemplo)[1]);
export const avisarSoloLectura = (t, motivo) => toast.info(t((AVISO[motivo] || AVISO.ejemplo)[0]));

// Instalación de Mongo → { archivo (v2, para editar), inst (= archivo.instalacion), f (recalculada), resultados, error }.
// error: los datos guardados no pasan la validación del motor (se muestran igual para poder corregirlos).
export function useEnergia(installation, opciones = {}) {
  const modoContexto = useModo();
  const modo = opciones.modo || modoContexto;
  return useMemo(() => {
    if (!installation) return { archivo: null, inst: null, f: null, resultados: null, error: null };
    const { archivo } = motor.mongoAV2(installation);
    const vista = motor.calcularVista(archivo, { modo });
    return { archivo, inst: archivo.instalacion, ...vista };
  }, [installation, modo]);
}

// Texto de los errores de validación que devuelve el motor o el servidor: [{seccion, fila, mensaje}].
export function textoErrores(errores) {
  return (errores || []).map((e) => (e.fila ? `${e.seccion}, fila ${e.fila}: ` : "") + e.mensaje);
}

// Guarda una o más secciones de la instalación. `cambios` va en formato v2 (p. ej. { operations: [...] }).
// Antes de mandar valida con el motor la instalación completa como quedaría (mismas reglas que el servidor).
export function useGuardarSeccion(installation, onSaved, { t = (k) => k } = {}) {
  const { accessToken } = useAuth();
  const soloLectura = useSoloLectura();
  const [saving, setSaving] = useState(false);
  const [errores, setErrores] = useState([]);

  // silencioso: sin aviso de «guardado» (cambios que se guardan al instante, como la selección de gráficos)
  const guardar = useCallback(async (cambios, { silencioso = false } = {}) => {
    if (!installation) return false;
    if (soloLectura) { avisarSoloLectura(t, soloLectura); return false; }
    const { archivo } = motor.mongoAV2(installation);
    const propuesto = { ...archivo, instalacion: { ...archivo.instalacion, ...cambios } };
    const errs = motor.validarInstalacion(propuesto);
    if (errs.length) {
      setErrores(errs);
      return false;
    }
    const { doc } = motor.v2AMongo({ instalacion: cambios });
    setSaving(true);
    try {
      const res = await energyPerformanceCtrl.updateEnergyPerformanceInstallation(accessToken, installation._id, doc);
      setErrores([]);
      if (!silencioso) toast.success(t("form_saved_successfully"));
      onSaved?.(res.installation);
      return true;
    } catch (error) {
      if (Array.isArray(error?.errors)) setErrores(error.errors);
      else toast.error(error?.message || error?.msg || t("server_connection_error"));
      return false;
    } finally {
      setSaving(false);
    }
  }, [installation, accessToken, onSaved, t, soloLectura]);

  return { guardar, saving, errores, limpiarErrores: () => setErrores([]) };
}
